import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type Company,
  type CompanyRole,
  type Invitation,
  type Member,
  type OfficeClient,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as written } from '../format';
import * as Icon from '../components/Icons';
import { roleLabel } from '../gender';

const INVITABLE_ROLES: CompanyRole[] = ['ACCOUNTANT', 'EMPLOYEE', 'BOARD_MEMBER', 'VIEWER', 'ADMIN', 'OWNER'];

/**
 * Co widzi każda rola — zestawienie z projektu.
 *
 * „—” oznacza, że pozycja w ogóle nie pojawia się w nawigacji tej osoby. Nie pokazujemy
 * wyszarzonych przycisków, których nie da się kliknąć.
 */
const COMPARED_ROLES: CompanyRole[] = ['OWNER', 'ADMIN', 'ACCOUNTANT', 'EMPLOYEE', 'BOARD_MEMBER', 'VIEWER'];

/*
 * Kolumny idą w kolejności COMPARED_ROLES. Wartości są przepisane z reguł w serwisach, a nie
 * wymyślone: członek zarządu nie występuje w żadnym sprawdzeniu uprawnień, więc ma czysty podgląd,
 * a administrator może wszędzie tam, gdzie właściciel.
 */
const PERMISSIONS: { key: 'documents' | 'books' | 'settings' | 'people' | 'billing'; cells: string[] }[] = [
  { key: 'documents', cells: ['full', 'full', 'full', 'add', 'view', 'view'] },
  { key: 'books', cells: ['full', 'full', 'full', '—', 'view', 'view'] },
  { key: 'settings', cells: ['full', 'full', 'full', '—', '—', '—'] },
  { key: 'people', cells: ['full', 'full', '—', '—', '—', '—'] },
  { key: 'billing', cells: ['full', 'full', '—', '—', '—', '—'] },
];

export function AccessPanel({
  company,
  t,
  locale,
  account,
  onChanged,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  account: { id: string };
  /** Wołane po zmianie ról — role bieżącej osoby przychodzą z listy firm i trzeba ją odświeżyć. */
  onChanged: () => void;
}) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invitations, setInvitations] = useState<Invitation[] | null>(null);
  /** Umowy z biurami: propozycje czekające na decyzję i biuro, które już prowadzi księgi. */
  const [agreements, setAgreements] = useState<OfficeClient[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CompanyRole>('ACCOUNTANT');
  const [ownDocumentsOnly, setOwnDocumentsOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [people, invited, offered] = await Promise.all([
        api.members(company.id),
        api.invitations(company.id),
        // Umowy z biurem widzi tylko właściciel i administrator — księgowej API odmawia, i to nie błąd
        api.officeAgreements(company.id).catch(() => [] as OfficeClient[]),
      ]);
      // API pomija puste pola, więc „brak daty zakończenia” to undefined, nie null
      setMembers(people.filter((member) => !member.validTo));
      setInvitations(invited);
      setAgreements(offered);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const manages = company.roles.some((granted) => granted === 'OWNER' || granted === 'ADMIN');
  const pending = (invitations ?? []).filter((invitation) => invitation.status === 'pending');

  /*
   * Kto może dziś prowadzić księgi tej firmy.
   *
   * Osoby przydzielone przez biuro nie pojawiają się na liście członków — ich dostęp bierze się
   * z umowy, nie z członkostwa. Bez sprawdzenia umów firma obsługiwana przez biuro wyglądałaby
   * na pozbawioną księgowej i dostawałaby ostrzeżenie bez powodu.
   */
  const someoneKeepsBooks =
    (members ?? []).some((member) => member.role === 'ACCOUNTANT') ||
    agreements.some((agreement) => agreement.status !== 'pending' && agreement.accountants.length > 0);
  const mayTakeRole = company.booksType === 'FULL' && manages && !company.roles.includes('ACCOUNTANT');
  const myAccesses = (members ?? []).filter((member) => member.userId === account.id).length;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.access.title}</h3>
          <p>{t.access.lead}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {/*
        * Pełne księgi prowadzi wyłącznie rola Księgowa. Właściciel może wziąć ją dla siebie —
        * prowadzenie ksiąg własnej firmy nie wymaga żadnego certyfikatu — ale ma wiedzieć, co
        * bierze: odpowiedzialność zostaje po jego stronie, a rola wymaga drugiego składnika.
        */}
      {mayTakeRole && (
        <div className={someoneKeepsBooks ? 'alert a-info' : 'alert a-warn'}>
          {someoneKeepsBooks ? <Icon.Info /> : <Icon.Warn />}
          <div>
            <b>{someoneKeepsBooks ? t.access.selfAccountant : t.access.noBookkeeper}</b>
            <p>{t.access.selfAccountantHint}</p>
          </div>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.addMember(company.id, account.id, 'ACCOUNTANT');
                onChanged();
              })
            }
          >
            {t.access.takeAccountantRole}
          </button>
        </div>
      )}

      {/*
        * Umowa z biurem stoi nad listą osób: propozycja, bo to decyzja czekająca na właściciela,
        * a obowiązująca — bo to ona, a nie członkostwo, wpuszcza księgowe biura do ksiąg.
        * Dostęp z biura nie pojawia się na liście osób, więc bez tego panelu firma nie wiedziałaby
        * nawet, kto w jej księgach siedzi.
        */}
      {agreements.map((agreement) =>
        agreement.status === 'pending' ? (
          <div className="panel" key={agreement.id}>
            <header>
              <h4>{t.access.officeProposals}</h4>
            </header>
            <div className="alert a-info">
              <Icon.Info />
              <div>
                <b>{t.access.officeProposalsHint}</b>
              </div>
            </div>
            <dl className="brk" style={{ maxWidth: 480 }}>
              <dt>{t.access.office}</dt>
              <dd>{agreement.officeName}</dd>
              <dt>{t.access.whoPays}</dt>
              <dd>{agreement.payer === 'office' ? t.access.payerOffice : t.access.payerCompany}</dd>
            </dl>
            <div className="bar2">
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => void run(() => api.acceptOfficeProposal(company.id, agreement.id))}
              >
                {t.access.acceptProposal}
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={busy}
                onClick={() => void run(() => api.rejectOfficeProposal(company.id, agreement.id))}
              >
                {t.access.rejectProposal}
              </button>
            </div>
          </div>
        ) : (
          <div className="panel" key={agreement.id}>
            <header>
              <h4>{t.access.servedBy}</h4>
              <div className="sp">
                <span className="chip st-ok">
                  <i />
                  {agreement.officeName}
                </span>
              </div>
            </header>
            <dl className="brk" style={{ maxWidth: 480 }}>
              <dt>{t.access.whoPays}</dt>
              <dd>{agreement.payer === 'office' ? t.access.payerOffice : t.access.payerCompany}</dd>
              <dt>{t.access.officeAccountants}</dt>
              <dd>{agreement.accountants.length > 0 ? agreement.accountants.join(', ') : t.access.nobodyYet}</dd>
            </dl>
            {manages && (
              <div className="bar2">
                <button
                  type="button"
                  className="btn danger"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(t.access.confirmEndOffice(agreement.officeName))) {
                      void run(() => api.endOfficeAgreement(company.id, agreement.id));
                    }
                  }}
                >
                  {t.access.endOffice}
                </button>
                <span className="hint">{t.access.endOfficeHint}</span>
              </div>
            )}
          </div>
        ),
      )}

      <div className="panel">
        <header>
          <h4>{t.access.people}</h4>
          <div className="sp">
            <span className="sm muted">{members?.length ?? 0}</span>
          </div>
        </header>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.access.person}</th>
                <th>{t.access.email}</th>
                <th>{t.access.role}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members?.map((member) => (
                <tr key={member.membershipId}>
                  <td>{member.fullName}</td>
                  <td className="mono">{member.email}</td>
                  <td>
                    <span className="st">
                      <span className="chip st-info">
                        <i />
                        {roleLabel(t, member.role, member.fullName)}
                      </span>
                      {/* Zakres widać przy osobie, a nie dopiero w formularzu zaproszenia */}
                      {member.ownDocumentsOnly && (
                        <span className="chip st-draft">{t.access.ownDocumentsOnlyShort}</span>
                      )}
                    </span>
                  </td>
                  <td className="act">
                    <div className="row">
                      {/*
                        * Przy własnym, jedynym dostępie przycisku nie ma. Kliknięcie zamykałoby
                        * drzwi od środka: otworzyć je mógłby już tylko ktoś inny. Serwer odmawia
                        * tak samo — ekran tylko nie kusi czymś, co i tak się nie uda.
                        */}
                      {manages && !(member.userId === account.id && myAccesses <= 1) && (
                        <button
                          type="button"
                          className="btn danger sm"
                          disabled={busy}
                          onClick={() => void run(() => api.removeMember(company.id, member.membershipId))}
                        >
                          {t.access.remove}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
          <header>
            <h4>{t.access.invitations}</h4>
          </header>

          {pending.length === 0 ? (
            <div className="empty" style={{ padding: 'var(--space-6)' }}>
              <b>{t.access.noInvitations}</b>
              <p>{t.access.hint}</p>
            </div>
          ) : (
            <div className="tbl">
              <table>
                <tbody>
                  {(invitations ?? []).map((invitation) => (
                    <tr key={invitation.id}>
                      <td className="mono">{invitation.email}</td>
                      <td>{t.access.roles[invitation.role]}</td>
                      <td>
                        <span className={invitation.status === 'pending' ? 'chip st-warn' : 'chip st-draft'}>
                          <i />
                          {t.access.statuses[invitation.status]}
                        </span>
                      </td>
                      <td className="doc">
                        {t.access.expires} {written(invitation.expiresAt, locale)}
                      </td>
                      <td className="act">
                        <div className="row">
                          {manages && invitation.status === 'pending' && (
                            <button
                              type="button"
                              className="btn ghost sm"
                              disabled={busy}
                              onClick={() => void run(() => api.revokeInvitation(company.id, invitation.id))}
                            >
                              {t.access.revoke}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {manages && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  await api.invite(company.id, email, role, locale, ownDocumentsOnly);
                  setEmail('');
                });
              }}
            >
              <div className="fields">
                <div className="field">
                  <label htmlFor="inviteEmail">{t.access.email}</label>
                  <input
                    id="inviteEmail"
                    className="inp"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="osoba@firma.pl"
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="inviteRole">{t.access.role}</label>
                  <select
                    id="inviteRole"
                    className="inp"
                    value={role}
                    onChange={(event) => setRole(event.target.value as CompanyRole)}
                  >
                    {INVITABLE_ROLES.map((value) => (
                      <option key={value} value={value}>
                        {t.access.roles[value]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/*
                * Zakres pytamy tylko przy pracowniku. Przy księgowej albo właścicielu połowa
                * dokumentów znaczyłaby połowę ksiąg — pole, którego nie wolno użyć, tylko myli.
                */}
              {role === 'EMPLOYEE' && (
                <div className="field">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={ownDocumentsOnly}
                      onChange={(event) => setOwnDocumentsOnly(event.target.checked)}
                    />
                    <span>{t.access.ownDocumentsOnly}</span>
                  </label>
                  <p className="sm muted">{t.access.ownDocumentsOnlyHint}</p>
                </div>
              )}
              <div className="bar2">
                <button type="submit" className="btn" disabled={busy || !email}>
                  {t.access.send}
                </button>
              </div>
            </form>
          )}
      </div>

      {/* Ról jest sześć, więc tabela jest szeroka — stoi osobno, na całej szerokości, pod zaproszeniami */}
      <div className="panel">
          <header>
            <h4>{t.access.whatEachRoleSees}</h4>
          </header>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.access.permission}</th>
                  {COMPARED_ROLES.map((role) => (
                    <th key={role}>{t.access.roles[role]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSIONS.map((row) => (
                  <tr key={row.key}>
                    <td>{t.access.permissions[row.key]}</td>
                    {row.cells.map((cell, index) => (
                      <td key={index} className={cell === '—' ? 'muted' : undefined}>
                        {cell === '—' ? '—' : t.access.levels[cell as 'full' | 'add' | 'view']}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="note">{t.access.permissionsNote}</p>
      </div>
    </>
  );
}
